import { useEffect, useState, useRef } from "react";
import { BrowserIcon, OsIcon, DeviceIcon, BotBadge } from "./icons";
import { isPrerendering } from "@/lib/prerender_flag";

// Mock-data helpers so the preview lives even when the user hasn't generated a site yet.
const SAMPLE_COUNTRIES = [
  { code: "US", name: "United States", flag: "🇺🇸" },
  { code: "IN", name: "India", flag: "🇮🇳" },
  { code: "GB", name: "United Kingdom", flag: "🇬🇧" },
  { code: "DE", name: "Germany", flag: "🇩🇪" },
  { code: "BR", name: "Brazil", flag: "🇧🇷" },
  { code: "JP", name: "Japan", flag: "🇯🇵" },
  { code: "CA", name: "Canada", flag: "🇨🇦" },
  { code: "FR", name: "France", flag: "🇫🇷" },
  { code: "AU", name: "Australia", flag: "🇦🇺" },
  { code: "NL", name: "Netherlands", flag: "🇳🇱" },
];
const BROWSERS = ["Chrome", "Safari", "Firefox", "Edge", "Opera"];
const OSES = ["Windows", "macOS", "Linux", "Android", "iOS"];
const DEVICES_BY_OS = { Windows: "desktop", macOS: "desktop", Linux: "desktop", Android: "mobile", iOS: "mobile" };
const REFERRERS = ["google.com", "twitter.com", "reddit.com", "facebook.com", "direct", "news.ycombinator.com"];
const PATHS = [
  { path: "/", title: "Home" },
  { path: "/blog", title: "Blog" },
  { path: "/blog/launch", title: "Launching our new platform" },
  { path: "/about", title: "About us" },
  { path: "/docs", title: "Documentation" },
  { path: "/contact", title: "Contact us" },
];

const rand = (arr) => arr[Math.floor(Math.random() * arr.length)];

// v60.102.9 — DETERMINISTIC FIRST PAINT.
//
// This preview used to build its opening six rows with Math.random() and
// new Date() inside useState's initialiser. The prerendered HTML therefore
// baked one random set of visitors, and the browser generated a different set
// during hydration — a guaranteed text mismatch. React threw error #418
// ("hydration failed"), which (a) logged a console exception on every page
// carrying this widget, capping Lighthouse Best Practices at 96 sitewide, and
// (b) made React discard the entire prerendered tree and re-render client-side,
// which is what pushed mobile LCP to ~4 s on pages that paint in 0.4 s.
//
// The fix is not to remove the liveness, it's to delay it: render a fixed set
// that the server and the first client paint agree on byte-for-byte, then swap
// in randomised visits in an effect, after hydration has committed. The user
// still sees a live feed within one tick; the crawler and Lighthouse see a
// clean hydration.
const SEED_VISITS = [
  { id: "seed-1", country_code: "US", country_name: "United States", flag: "🇺🇸", browser: "Chrome", os: "macOS", device: "desktop", referrer: "google.com", landing_page: "/", title: "Home", is_bot: false, ts_label: "just now" },
  { id: "seed-2", country_code: "IN", country_name: "India", flag: "🇮🇳", browser: "Chrome", os: "Android", device: "mobile", referrer: "twitter.com", landing_page: "/blog", title: "Blog", is_bot: false, ts_label: "8s ago" },
  { id: "seed-3", country_code: "GB", country_name: "United Kingdom", flag: "🇬🇧", browser: "Safari", os: "iOS", device: "mobile", referrer: "direct", landing_page: "/blog/launch", title: "Launching our new platform", is_bot: false, ts_label: "24s ago" },
  { id: "seed-4", country_code: "DE", country_name: "Germany", flag: "🇩🇪", browser: "Firefox", os: "Windows", device: "desktop", referrer: "news.ycombinator.com", landing_page: "/docs", title: "Documentation", is_bot: false, ts_label: "1m ago" },
  { id: "seed-5", country_code: "BR", country_name: "Brazil", flag: "🇧🇷", browser: "Chrome", os: "Windows", device: "desktop", referrer: "reddit.com", landing_page: "/about", title: "About us", is_bot: true, ts_label: "2m ago" },
  { id: "seed-6", country_code: "JP", country_name: "Japan", flag: "🇯🇵", browser: "Edge", os: "Windows", device: "desktop", referrer: "facebook.com", landing_page: "/contact", title: "Contact us", is_bot: false, ts_label: "3m ago" },
].map((v) => ({ ...v, country_flag: v.flag }));

function mkVisit() {
  const c = rand(SAMPLE_COUNTRIES);
  const p = rand(PATHS);
  const osName = rand(OSES);
  return {
    id: Math.random().toString(36).slice(2, 11),
    country_flag: c.flag,
    country_name: c.name,
    country_code: c.code,
    browser: rand(BROWSERS),
    os: osName,
    device: DEVICES_BY_OS[osName] || "desktop",
    referrer: rand(REFERRERS),
    landing_page: p.path,
    title: p.title,
    is_bot: Math.random() < 0.18, // Slightly higher so bot icon is visible in preview
    created_at: new Date().toISOString(),
  };
}

function relTime(iso) {
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 5) return "just now";
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function cleanReferrer(r) {
  const v = String(r || "").trim();
  if (!v || v.toLowerCase() === "direct") return "Direct";
  return v.replace(/^https?:\/\//i, "").replace(/^www\./i, "").split("/")[0];
}

function pageLabel(v) {
  const t = (v.title || "").trim();
  if (t) return t.length > 40 ? t.slice(0, 38) + "…" : t;
  const p = v.landing_page || "/";
  if (p === "/") return "Home";
  return p.length > 40 ? p.slice(0, 38) + "…" : p;
}

export default function LiveTrafficFeedPreview({ cfg, initialVisits = null }) {
  const [visits, setVisits] = useState(() => initialVisits || SEED_VISITS);
  const [pulseIds, setPulseIds] = useState(() => new Set());
  const [, force] = useState(0);
  const tickRef = useRef(null);

  // Swap the deterministic seed rows for randomised ones AFTER hydration.
  // Skipped while prerendering (see prerender.js) so the baked HTML and the
  // browser's first paint agree, which is what keeps hydration clean.
  useEffect(() => {
    if (initialVisits || isPrerendering()) return;
    setVisits(Array.from({ length: 6 }, () => mkVisit()));
  }, [initialVisits]);

  // v60.35 — Throttle the relative-timestamp re-render from 1 s to 5 s and
  // pause when the preview is offscreen / the tab is hidden.
  //
  // Why: the original 1 s tick re-rendered the whole preview card every
  // second purely to roll "5s ago" → "6s ago". The card lives in the
  // hero (LCP region), and each commit forces the browser to lay out
  // the flex/grid timestamp cells whose text width changes (1-digit →
  // 2-digit second counter). On production PSI this added up to ~103 ms
  // of "unattributed forced reflow" over Lighthouse's ~10 s sampling
  // window — exactly the residual reflow flag we couldn't pin down
  // after the Testimonials fix.
  //
  // 5 s relative-time resolution is invisible to a user (the labels
  // jump by 5 s instead of 1 s — still feels "live"), and the new visit
  // arrival ticker (the `schedule` effect below) is the actual liveness
  // signal anyway. Pausing while offscreen frees the main thread once
  // the hero is no longer the user's focus.
  useEffect(() => {
    if (isPrerendering()) return undefined;
    let id;
    const start = () => { if (!id) id = setInterval(() => force((x) => x + 1), 5000); };
    const stop = () => { if (id) { clearInterval(id); id = null; } };
    start();
    const onVis = () => { if (document.hidden) stop(); else start(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { stop(); document.removeEventListener("visibilitychange", onVis); };
  }, []);

  useEffect(() => {
    if (isPrerendering()) return undefined;
    const schedule = () => {
      const delay = 1800 + Math.random() * 3500;
      tickRef.current = setTimeout(() => {
        const nv = mkVisit();
        setVisits((v) => [nv, ...v].slice(0, cfg.rows || 10));
        setPulseIds((s) => new Set(s).add(nv.id));
        setTimeout(() => {
          setPulseIds((s) => {
            const next = new Set(s);
            next.delete(nv.id);
            return next;
          });
        }, 10000);
        schedule();
      }, delay);
    };
    schedule();
    return () => clearTimeout(tickRef.current);
  }, [cfg.rows]);

  const c = cfg;
  // Tiny inline label i18n — mirrors the actual widget script (traffic_feed.js)
  // so the preview matches what the user will deploy. Only labels visible
  // in the preview need translation; everything else flows from the widget.
  const L = (() => {
    const d = {
      en: { title: "Live Traffic", live: "live", from: "From", footer: "Live traffic widget · FeedPulse" },
      es: { title: "Tráfico en vivo", live: "en vivo", from: "Desde", footer: "Widget de tráfico en vivo · FeedPulse" },
      fr: { title: "Trafic en direct", live: "en direct", from: "Depuis", footer: "Widget de trafic en direct · FeedPulse" },
      de: { title: "Live-Traffic", live: "live", from: "Von", footer: "Live-Traffic-Widget · FeedPulse" },
      hi: { title: "लाइव ट्रैफ़िक", live: "लाइव", from: "से", footer: "लाइव ट्रैफ़िक विजेट · FeedPulse" },
      pt: { title: "Tráfego ao vivo", live: "ao vivo", from: "De", footer: "Widget de tráfego ao vivo · FeedPulse" },
      it: { title: "Traffico live", live: "live", from: "Da", footer: "Widget traffico live · FeedPulse" },
      ru: { title: "Живой трафик", live: "лайв", from: "Из", footer: "Виджет живого трафика · FeedPulse" },
      ja: { title: "ライブトラフィック", live: "ライブ", from: "から", footer: "ライブトラフィックウィジェット · FeedPulse" },
      zh: { title: "实时流量", live: "实时", from: "来自", footer: "实时流量小工具 · FeedPulse" },
      ar: { title: "حركة المرور المباشرة", live: "مباشر", from: "من", footer: "أداة حركة المرور المباشرة · FeedPulse" },
      nl: { title: "Live verkeer", live: "live", from: "Van", footer: "Live verkeer-widget · FeedPulse" },
    };
    return d[c.lang] || d.en;
  })();
  return (
    <div
      data-testid="preview-traffic-feed"
      style={{
        width: c.width,
        maxWidth: "100%",
        background: c.bg_color,
        color: c.text_color,
        border: `1px solid ${c.border_color}`,
        fontFamily: `${c.font}, system-ui, sans-serif`,
        fontSize: 12,
        // v60.69 — CSS containment. Tells the browser this widget's
        // layout/style/paint is fully independent of the surrounding
        // document. Animations + state changes inside no longer
        // trigger document-wide reflow; the engine only re-layouts
        // this subtree. Single biggest lever for homepage Style &
        // Layout cost (was 949 ms in Lighthouse trace).
        contain: "layout style paint",
      }}
    >
      {/* v60.69 — animation iteration count 10 → 3. Was running the
          highlight pulse 10× per visit (10 s of continuous keyframes
          per row). 3 iterations (3 s of pulsing) is still very visible
          but cuts the per-visit animation cost by 70%. */}
      <style>{`@keyframes ltf-newvisit-r{0%,50%{background-color:#38bdf8;}100%{background-color:transparent;}}.ltf-new-r{animation:ltf-newvisit-r 1s ease-out 3;}`}</style>
      <div
        style={{
          background: c.header_bg,
          color: c.header_fg,
          padding: "8px 10px",
          fontWeight: 700,
          letterSpacing: ".06em",
          textTransform: "uppercase",
          fontSize: 11,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <span>{L.title}</span>
        <span className="flex items-center gap-1.5">
          <span
            style={{ display: "inline-block", width: 7, height: 7, borderRadius: 9999, background: "#00E676" }}
            className="animate-blink-dot"
          />
          {L.live}
        </span>
      </div>
      <div>
        {visits.map((v) => (
          <div
            key={v.id}
            className={pulseIds.has(v.id) ? "ltf-new-r" : ""}
            style={{
              display: "flex",
              gap: 7,
              alignItems: "center",
              padding: "7px 10px",
              borderTop: `1px solid ${c.border_color}`,
            }}
          >
            <span style={{ fontSize: 17, lineHeight: 1, flexShrink: 0 }} aria-label={v.country_name}>
              {v.country_flag}
            </span>
            <div style={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
              <div style={{ display: "flex", gap: 5, alignItems: "center", fontSize: 11, opacity: 0.85, flexWrap: "nowrap" }}>
                <BrowserIcon name={v.browser} />
                <OsIcon name={v.os} />
                <DeviceIcon device={v.device} os={v.os} />
                {v.is_bot && <BotBadge />}
                <span style={{ marginLeft: "auto", opacity: 0.8, fontSize: 10, whiteSpace: "nowrap", fontWeight: 500 }}>{v.ts_label || relTime(v.created_at)}</span>
              </div>
              <div style={{ fontSize: 12, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", marginTop: 2 }}>
                {L.from}{" "}
                <a href="#0" onClick={(e) => e.preventDefault()} style={{ color: c.link_color, textDecoration: "none", fontWeight: 500 }}>
                  {cleanReferrer(v.referrer)}
                </a>{" "}
                → <span style={{ fontWeight: 600 }} title={v.landing_page}>{pageLabel(v)}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
      <BadgePreview style={c.badge_style} borderColor={c.border_color} footerLabel={L.footer} />
    </div>
  );
}

function BadgePreview({ style, borderColor, footerLabel }) {
  const s = (style || "minimal").toLowerCase();
  if (s === "pill") {
    return (
      <div style={{ padding: "8px 10px", borderTop: `1px solid ${borderColor}`, display: "flex", justifyContent: "center" }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "#0A0A0A", color: "#fff", padding: "4px 10px", borderRadius: 999, fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" }}>
          <span style={{ width: 6, height: 6, borderRadius: 999, background: "#00E676", display: "inline-block" }} />
          FeedPulse
        </span>
      </div>
    );
  }
  if (s === "animated") {
    return (
      <div style={{ padding: "6px 10px", borderTop: `1px solid ${borderColor}`, fontSize: 10, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, opacity: 0.85 }}>
        <span style={{ width: 6, height: 6, borderRadius: 999, background: "#00E676", display: "inline-block", animation: "fpBadgePulse 1.6s ease-in-out infinite" }} />
        <span style={{ opacity: 0.75 }}>Powered by FeedPulse</span>
        <style>{`@keyframes fpBadgePulse{0%,100%{opacity:1}50%{opacity:.4}}`}</style>
      </div>
    );
  }
  if (s === "icon") {
    return (
      <div style={{ padding: "6px 10px", borderTop: `1px solid ${borderColor}`, fontSize: 10, opacity: 0.7, display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
        <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 14, height: 14, background: "#002FA7", color: "#fff", fontWeight: 900, fontSize: 9 }}>F</span>
        <span>FeedPulse</span>
      </div>
    );
  }
  // minimal (default)
  return (
    <div style={{ padding: "6px 10px", borderTop: `1px solid ${borderColor}`, fontSize: 10, opacity: 0.7, textAlign: "center" }}>
      {footerLabel || "Live traffic widget · FeedPulse"}
    </div>
  );
}
